import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { Typography } from '@mui/material';
import { generateSourceHandleId } from '@utils/flows';
import { IS_TEST_MODE } from 'constants/global';
import Elk, { ElkExtendedEdge, ElkNode, ElkPort } from 'elkjs';
import React, { useCallback } from 'react';
import { ControlButton, Edge, Node, useReactFlow } from 'reactflow';
import { IEdgeData, ITaskNodeData, START_TASK_ID } from 'types/flows';

const DEFAULT_WIDTH = 200;
const DEFAULT_HEIGHT = 120;

// docs in https://dagobertgans.github.io/docgen/_pages/org-eclipse-elk-layered.html
const elkOptions = {
	'elk.algorithm': 'layered',
	'elk.spacing.nodeNode': '150',
	'elk.direction': 'RIGHT',
	'nodePlacement.bk.fixedAlignment': 'BALANCED',
	'elk.layered.feedbackEdges': 'true',
	'considerModelOrder.components': 'INSIDE_PORT_SIDE_GROUPS',
	'elk.layered.spacing.nodeNodeBetweenLayers': '150'
};

const elk = new Elk({
	defaultLayoutOptions: elkOptions
});

const convertToElkGraph = (
	nodes: Array<Node<ITaskNodeData>>,
	edges: Array<Edge<IEdgeData>>
) => {
	const elkNodes: ElkNode[] = [];
	const elkEdges: ElkExtendedEdge[] = [];
	const startTask =
		nodes
			.find((node) => node.id === START_TASK_ID)
			?.data.task?.next?.rules.pop()?.taskId || '0';
	let startNodeIndex = 0;

	nodes.forEach((flowNode, index) => {
		const width = flowNode.width || DEFAULT_WIDTH;
		const layoutOptions: Record<string, string> = {};
		if (flowNode.id === START_TASK_ID) {
			// first take should always be alone to be easily noticiable.
			layoutOptions['layering.layerConstraint'] = 'FIRST_SEPARATE';
		} else if (flowNode.id === startTask) {
			// actual first running task should always follow the start task.
			startNodeIndex = index;
			layoutOptions['layering.layerConstraint'] = 'FIRST';
		}
		elkNodes.push({
			id: flowNode.id,
			width,
			height: flowNode.height || DEFAULT_HEIGHT,
			labels: [
				{ text: flowNode.id, x: width - 15 },
				...(flowNode.data.task?.name
					? [{ text: flowNode.data.task?.name }]
					: [])
			],
			layoutOptions,
			ports: [
				...(flowNode.data.interactions
					?.filter((interaction) => interaction.nextTask)
					.map<ElkPort>((interaction, i) => ({
						id: generateSourceHandleId(flowNode.id, interaction.id),
						y: 150 + i * 100,
						properties: { 'port.side': 'WEST' },
						labels: [
							{ text: interaction.label || interaction.icon || interaction.id }
						],
						width: 10,
						height: 10
					})) || [])
			]
		});
	});
	edges.forEach((flowEdge) => {
		elkEdges.push({
			id: flowEdge.id,
			targets: [flowEdge.target],
			sources: [
				flowEdge.source === START_TASK_ID
					? START_TASK_ID
					: flowEdge.sourceHandle!
			]
		});
	});

	elkNodes.push(elkNodes.splice(startNodeIndex, 1)[0]);
	if (!IS_TEST_MODE) {
		// eslint-disable-next-line
		console.log(`visualizer at "https://rtsys.informatik.uni-kiel.de/elklive/json.html" copy the following: {
			id: "root",
			layoutOptions: ${JSON.stringify(elkOptions, null, 2)},
			children: ${JSON.stringify(elkNodes)},
			edges: ${JSON.stringify(elkEdges)}
		}`);
	}
	return { elkNodes, elkEdges };
};

const autoAlignLayout = async (
	nodes: Array<Node<ITaskNodeData>>,
	edges: Array<Edge<IEdgeData>>
): Promise<Array<Node>> => {
	const { elkEdges, elkNodes } = convertToElkGraph(nodes, edges);
	const newGraph = await elk.layout({
		id: 'start',
		children: elkNodes,
		edges: elkEdges
	});

	// convert elkjs nodes to reactflow nodes with the new position.
	return nodes.map((reactFlowNode) => {
		const currentElkNode = newGraph?.children?.find(
			(n) => n.id === reactFlowNode.id
		);
		if (
			currentElkNode?.x &&
			currentElkNode?.y &&
			currentElkNode?.width &&
			currentElkNode?.height
		) {
			reactFlowNode.position = {
				x: currentElkNode.x,
				y: currentElkNode.y
			};
		}
		return reactFlowNode;
	});
};

const AutoLayoutControl = () => {
	const { setNodes, getNodes, getEdges, fitView } = useReactFlow();
	const fitCallback = useCallback(async () => {
		setNodes(await autoAlignLayout(getNodes(), getEdges()));
		setTimeout(() => fitView({ duration: 600, maxZoom: 1 }), 100);
	}, [setNodes, getNodes, fitView, getEdges]);

	return (
		<ControlButton
			onClick={fitCallback}
			data-testid="layout-button"
			aria-label={intl.tr('button.layout')}
		>
			<Typography variant="inherit" component="span" color="text.secondary">
				<AccountTreeIcon sx={{ transform: 'rotate(180deg)' }} />
			</Typography>
		</ControlButton>
	);
};

export default AutoLayoutControl;
export { elk };
